import { useState, useEffect, useCallback } from 'react';
import { rpcCall } from '../../api/rpc';

/** Shape of persisted user preferences */
export interface UserPreferences {
  // Contact
  phone?: string;
  email?: string;
  pager?: string;
  fax?: string;
  location?: string;
  // Notifications
  emailNotifications?: boolean;
  browserNotifications?: boolean;
  urgentFindings?: boolean;
  newAssignments?: boolean;
  soundAlerts?: boolean;
  // Application
  theme?: string;
  pageSize?: string;
  autoRefresh?: boolean;
  showDetailPanel?: boolean;
  showAggregation?: boolean;
  compactMode?: boolean;
  // Monitor
  monitorLayout?: string;
  // Columns — which worklist columns are visible
  visibleColumns?: string[];
  // Tabs — which default worklist tabs are visible
  visibleTabs?: string[];
  // Details Layout — ordered list of visible detail panel fields
  detailPanelFields?: string[];
}

const PREFS_KEY = 'userPreferences';

const DEFAULT_PREFS: UserPreferences = {
  emailNotifications: true,
  browserNotifications: true,
  urgentFindings: false,
  newAssignments: false,
  soundAlerts: false,
  theme: 'blue',
  pageSize: '100',
  autoRefresh: true,
  showDetailPanel: true,
  showAggregation: true,
  compactMode: false,
  monitorLayout: 'single',
};

/**
 * Hook to load and save user preferences via getjson/setjson RPC methods.
 * These use ConfigStorageManager on the backend.
 */
export function usePreferences(enabled: boolean) {
  const [prefs, setPrefs] = useState<UserPreferences>(DEFAULT_PREFS);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);

  // Load preferences when enabled
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;

    (async () => {
      try {
        const result = await rpcCall<UserPreferences | null>('getjson', {
          key: PREFS_KEY,
        });
        if (!cancelled && result != null && typeof result === 'object') {
          setPrefs({ ...DEFAULT_PREFS, ...result });
        }
      } catch {
        // getjson not found or no saved prefs — keep defaults
      }
      if (!cancelled) setLoaded(true);
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled]);

  const updatePref = useCallback(
    <K extends keyof UserPreferences>(key: K, value: UserPreferences[K]) => {
      setPrefs((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  const save = useCallback(async () => {
    setSaving(true);
    try {
      await rpcCall('setjson', { key: PREFS_KEY, value: prefs });
    } catch (e) {
      console.error('Failed to save preferences:', e);
    } finally {
      setSaving(false);
    }
  }, [prefs]);

  return { prefs, updatePref, save, saving, loaded };
}
